import {
  Box,
  Button,
  VStack,
  Input,
  useToast,
  Text,
  Checkbox,
  useColorModeValue,
  Heading,
  HStack,
} from "@chakra-ui/react";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../Routers/TenantContextProvider";

interface PasswordChangeRequest {
  old_password: string;
  new_password: string;
  confirm_new_password: string;
}

interface ChangePasswordProps {
  handleLogout: () => void;
}

const ChangePassword = ({ handleLogout }: ChangePasswordProps) => {
  const [passwordChangeData, setPasswordChangeData] =
    useState<PasswordChangeRequest>({
      old_password: "",
      new_password: "",
      confirm_new_password: "",
    });

  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isInitialPassword, setIsInitialPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const bannerColor = useColorModeValue("yellow.100", "yellow.700");

  // Prevent authentication checks / effects from running again
  // after a successful password change.
  const passwordChangedRef = useRef(false);

  /*
   * Read the current token once when the component mounts.
   *
   * IMPORTANT:
   * Absence of a token is not treated as a toast-worthy error here.
   * The router is responsible for authentication and redirection.
   */
  useEffect(() => {
    if (passwordChangedRef.current) {
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    try {
      const payload = token.split(".")[1];

      if (!payload) {
        return;
      }

      const decodedToken = JSON.parse(atob(payload));

      setIsInitialPassword(
        decodedToken?.is_initial_password === true
      );
    } catch (error) {
      console.error("Unable to decode authentication token:", error);
    }
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;

    setPasswordChangeData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "new_password") {
      validatePassword(value);
    }
  };

  const validatePassword = (password: string) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

    if (!passwordRegex.test(password)) {
      setPasswordError(
        "Password must be at least 8 characters long, contain one uppercase letter, one lowercase letter, one number, and one special character (@ $ ! % * ? & #)."
      );
    } else {
      setPasswordError(null);
    }
  };

  const handleChangePassword = async () => {
    if (isSubmitting || passwordChangedRef.current) {
      return;
    }

    const {
      old_password,
      new_password,
      confirm_new_password,
    } = passwordChangeData;

    if (!old_password || !new_password || !confirm_new_password) {
      toast({
        title: "Please complete all password fields.",
        status: "error",
      });
      return;
    }

    if (new_password !== confirm_new_password) {
      toast({
        title: "Passwords do not match.",
        status: "error",
      });
      return;
    }

    if (passwordError) {
      toast({
        title: "Please resolve the password errors before submitting.",
        status: "error",
      });
      return;
    }

    const token = localStorage.getItem("token");

    /*
     * Do not show a toast here.
     *
     * If the token has disappeared, the router/authentication layer
     * will take care of the redirect.
     */
    if (!token) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      old_password,
      new_password,
    };

    try {
      const apiurl =
        process.env.REACT_APP_API_URL ||
        "https://default-url.com";

      await axios.put(
        `${apiurl}/change-password`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      /*
       * Mark this operation as completed BEFORE triggering logout.
       *
       * This is important because logout changes authentication state,
       * which can cause the surrounding router tree to re-render.
       */
      passwordChangedRef.current = true;

      toast({
        title: "Password Updated Successfully",
        status: "success",
      });

      setPasswordChangeData({
        old_password: "",
        new_password: "",
        confirm_new_password: "",
      });

      /*
       * There is ONE logout mechanism.
       *
       * Do not also remove the token here.
       * Do not also navigate here.
       *
       * handleLogout() already:
       *   - revokes refresh token
       *   - removes tokens
       *   - resets authentication state
       *   - navigates to the appropriate login page
       */
      await Promise.resolve(handleLogout());
    } catch (error) {
      console.error("Error updating password:", error);

      /*
       * Only reset submitting state when the password operation
       * actually failed. On success, logout takes over.
       */
      setIsSubmitting(false);

      if (axios.isAxiosError(error)) {
        const detail =
          error.response?.data?.detail;

        toast({
          title: "Password Change Request Failed",
          description:
            typeof detail === "string"
              ? detail
              : "Unable to update your password. Please try again.",
          status: "error",
        });
      } else {
        toast({
          title: "Password Change Request Failed",
          description:
            "Unable to update your password. Please try again.",
          status: "error",
        });
      }
    }
  };

  return (
    <Box>
      {/* Header / banner */}
      <Box
        bgColor={bannerColor}
        p={4}
        minH="70px"
      >
        <HStack>
          <Heading size="sm">
            Hello!
          </Heading>

          <Text
            fontWeight="normal"
            fontSize="md"
          >
            {isInitialPassword
              ? "Please change your initial password and login again to continue."
              : "Change your password."}
          </Text>
        </HStack>
      </Box>

      {/* Password form */}
      <Box
        maxW="350px"
        mx="auto"
        my={8}
        border="1px solid"
        borderColor="gray.400"
        borderRadius="md"
        mt={20}
        p={4}
        mb={6}
      >
        <VStack spacing={4}>
          <Input
            placeholder="Current Password"
            name="old_password"
            type={showPassword ? "text" : "password"}
            value={passwordChangeData.old_password}
            onChange={handleChange}
            isDisabled={isSubmitting}
            required
          />

          <Input
            placeholder="New Password"
            name="new_password"
            type={showPassword ? "text" : "password"}
            value={passwordChangeData.new_password}
            onChange={handleChange}
            isDisabled={isSubmitting}
            required
          />

          {passwordError && (
            <Text
              color="red.500"
              fontSize="sm"
            >
              {passwordError}
            </Text>
          )}

          <Input
            placeholder="Confirm New Password"
            name="confirm_new_password"
            type={showPassword ? "text" : "password"}
            value={passwordChangeData.confirm_new_password}
            onChange={handleChange}
            isDisabled={isSubmitting}
            required
          />

          <Checkbox
            isChecked={showPassword}
            onChange={(e) =>
              setShowPassword(e.target.checked)
            }
            isDisabled={isSubmitting}
          >
            Show Password
          </Checkbox>
        </VStack>

        <VStack mt={5} spacing={4}>
          <Button
            border="2px solid"
            borderColor="blue.200"
            borderRadius="md"
            width={40}
            onClick={handleChangePassword}
            isDisabled={
              isSubmitting || !!passwordError
            }
            isLoading={isSubmitting}
            loadingText="Updating..."
          >
            Change Password
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default ChangePassword;